/**
 * Arbiter textarea input field https://www.figma.com/design/iZHj2I36zd9i8nRbWKw4ZK/%E2%9D%96-Arbiter?node-id=1238-278&t=erSoAzUBb9k4GD7O-4
 */

import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import classNames from "classnames";
import {
  forwardRef,
  memo,
  ReactNode,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Flex } from "../../flex";
import { Text } from "../../text";
import { SpacingValue } from "../../theme/box";
import {
  textClasses,
  TextColorValue,
  TextProps,
  TextSizeValue,
} from "../../theme/typography";
import { DescriptionText, LabelText } from "../common/descriptors";
import * as RedoTextareaInputFieldCss from "./redo-textarea-input-field.module.css";
import {
  RedoTextareaValidation,
  useTextareaIssuePopover,
  useTextareaValidation,
} from "./redo-textarea-validation";
import {
  TextareaHighlightLayer,
  TextareaIssuePopover,
  TextareaValidationStatusIcon,
} from "./redo-textarea-validation-overlay";

export enum RedoTextareaInputFieldSize {
  SMALL = "small",
  REGULAR = "regular",
}

export enum RedoTextareaInputFieldTheme {
  DEFAULT = "default",
  DESTRUCTIVE = "destructive",
}

export interface RedoTextareaInputFieldProps {
  "data-dd-action-name"?: string;
  size?: RedoTextareaInputFieldSize;
  theme?: RedoTextareaInputFieldTheme;
  disabled?: boolean;
  readOnly?: boolean;
  placeholder?: string;
  value: string;
  setValue(value: string): void;
  label?: string;
  className?: string;
  hintText?: string;
  dangerousStyleThatShouldOnlyBeUsedForMerchantBranding?: React.CSSProperties;
  required?: boolean;
  restrictWidth?: boolean;
  maxLength?: number;
  maxHeight?: number;
  fullWidth?: boolean;
  containerHeight?: string;
  autoExpand?: boolean;
  autoFocus?: boolean;
  monospace?: boolean;
  errorText?: string;
  onCursorPositionChange?: (position: number) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onFocus?: React.FocusEventHandler<HTMLTextAreaElement>;
  onBlur?: React.FocusEventHandler<HTMLTextAreaElement>;
  onPaste?: React.ClipboardEventHandler<HTMLTextAreaElement>;
  style?: React.CSSProperties;
  resizable?: boolean;
  spellCheck?: boolean;
  toolbar?: ReactNode;
  actionButton?: ReactNode;
  uiSelector?: string;
  /** Debounced validation that highlights flagged text and shows a status icon. */
  validation?: RedoTextareaValidation;
}

export const RedoTextareaInputField = memo(
  forwardRef<HTMLTextAreaElement, RedoTextareaInputFieldProps>(
    function RedoTextareaInputField(
      {
        size = RedoTextareaInputFieldSize.SMALL,
        theme = RedoTextareaInputFieldTheme.DEFAULT,
        value,
        placeholder,
        setValue,
        disabled,
        readOnly,
        label,
        containerHeight,
        className,
        hintText,
        dangerousStyleThatShouldOnlyBeUsedForMerchantBranding,
        required,
        restrictWidth,
        maxLength,
        errorText,
        fullWidth = false,
        autoExpand = false,
        autoFocus = false,
        monospace = false,
        resizable = false,
        onCursorPositionChange,
        onKeyDown,
        onFocus,
        onBlur,
        onPaste,
        style,
        spellCheck,
        toolbar,
        actionButton,
        uiSelector,
        validation,
        "data-dd-action-name": actionName,
      }: RedoTextareaInputFieldProps,
      ref: React.ForwardedRef<HTMLTextAreaElement>,
    ) {
      const { t } = useTranslation();
      const textareaId = `redo-textarea-input-field-${useId()}`;
      const textareaRef = useRef<HTMLTextAreaElement>(null);

      useImperativeHandle<
        HTMLTextAreaElement | null,
        HTMLTextAreaElement | null
      >(ref, () => textareaRef.current);

      const adjustHeight = useHandler((textarea: HTMLTextAreaElement) => {
        if (autoExpand) {
          textarea.style.height = "auto";
          textarea.style.height = `${textarea.scrollHeight}px`;
        }
      });

      // Recalculate height when container width changes (e.g., during slide panel animations)
      useEffect(() => {
        if (!autoExpand || !textareaRef.current) {
          return;
        }

        const textarea = textareaRef.current;
        let rafId: number | null = null;

        const resizeObserver = new ResizeObserver(() => {
          // Use requestAnimationFrame to defer height adjustment and avoid
          // ResizeObserver loop errors when modifying the observed element
          if (rafId !== null) {
            cancelAnimationFrame(rafId);
          }
          rafId = requestAnimationFrame(() => {
            adjustHeight(textarea);
            rafId = null;
          });
        });

        resizeObserver.observe(textarea);

        return () => {
          if (rafId !== null) {
            cancelAnimationFrame(rafId);
          }
          resizeObserver.disconnect();
        };
      }, [autoExpand, adjustHeight]);

      // Handle height adjustment when value changes
      useEffect(() => {
        if (autoExpand && textareaRef.current) {
          adjustHeight(textareaRef.current);
        }
      }, [value, autoExpand, adjustHeight]);

      function handleInputContentChange(
        event: React.ChangeEvent<HTMLTextAreaElement>,
      ) {
        setValue(event.target.value);
        if (autoExpand) {
          adjustHeight(event.target);
        }
      }

      const {
        status: validationStatus,
        issues,
        checked: validationChecked,
        ignore: ignoreIssue,
        markFixed,
      } = useTextareaValidation(value, validation);
      const issuePopover = useTextareaIssuePopover({
        value,
        setValue,
        issues,
        ignore: ignoreIssue,
        textareaRef,
        onBlur,
        onKeyDown,
        resolveFix: validation?.resolveFix,
        onFixApplied: markFixed,
      });
      const [activeMark, setActiveMark] = useState<HTMLElement | null>(null);
      const validationMessageId = `${textareaId}-validation`;
      const validationAnnouncement = issuePopover.caretIssue
        ? `${issuePopover.caretIssue.message} ${t("Press Tab to review.", { context: "text validation" })}`
        : issues.length > 0
          ? t("Review the highlighted text", { context: "text validation" })
          : validationChecked
            ? t("No issues found", { context: "text validation" })
            : "";

      const textClassName = classNames(
        textClasses({
          ...sizeToTextProps[size],
          fontFamily: monospace ? "monospace" : undefined,
        }),
      );
      const showToolbar = !!toolbar && !readOnly;

      const handleCursorPositionChange = useHandler(
        (event: React.SyntheticEvent<HTMLTextAreaElement>) => {
          if (onCursorPositionChange) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
            const target = event.target as HTMLTextAreaElement;
            onCursorPositionChange(target.selectionStart);
          }
        },
      );

      return (
        <Flex
          className={classNames(
            restrictWidth && RedoTextareaInputFieldCss.containerWidth,
            !autoExpand && !resizable && RedoTextareaInputFieldCss.container,
            autoExpand && RedoTextareaInputFieldCss.autoExpand,
            fullWidth && RedoTextareaInputFieldCss.fullWidth,
          )}
          data-arbiter="RedoTextareaInputField"
          data-ui={uiSelector}
          dir="column"
          gap="sm"
          grow={fullWidth ? 1 : undefined}
          style={
            containerHeight && !autoExpand
              ? resizable
                ? { minHeight: containerHeight, ...style }
                : {
                    height: containerHeight,
                    minHeight: containerHeight,
                    ...style,
                  }
              : style
          }
        >
          <LabelText
            htmlFor={textareaId}
            label={label}
            size={sizeToLabelTextProps[size]}
          />
          <Flex
            className={classNames(
              (disabled || readOnly) && RedoTextareaInputFieldCss.disabled,
              RedoTextareaInputFieldCss.inputWrapper,
              issues.length > 0 && RedoTextareaInputFieldCss.error,
              sizeStyles[size],
              className,
            )}
            data-portal-selector="redo-input"
            dir="column"
            gap="xs"
            grow={1}
            justify="space-between"
            px={sizeToPx[size]}
            py={sizeToPy[size]}
            style={dangerousStyleThatShouldOnlyBeUsedForMerchantBranding}
          >
            <Flex
              align="stretch"
              className={
                validation
                  ? RedoTextareaInputFieldCss.highlightContainer
                  : undefined
              }
              grow={1}
            >
              <textarea
                aria-describedby={validation ? validationMessageId : undefined}
                aria-invalid={validation ? issues.length > 0 : undefined}
                autoFocus={autoFocus}
                className={classNames(
                  textClassName,
                  RedoTextareaInputFieldCss.textarea,
                  validation && RedoTextareaInputFieldCss.validatedTextarea,
                )}
                data-dd-action-name={actionName}
                disabled={disabled}
                id={textareaId}
                maxLength={maxLength}
                onBlur={
                  validation ? issuePopover.textareaHandlers.onBlur : onBlur
                }
                onChange={handleInputContentChange}
                onClick={handleCursorPositionChange}
                onFocus={onFocus}
                onKeyDown={
                  validation
                    ? issuePopover.textareaHandlers.onKeyDown
                    : onKeyDown
                }
                onKeyUp={handleCursorPositionChange}
                onPaste={onPaste}
                onSelect={
                  validation
                    ? issuePopover.textareaHandlers.onSelect
                    : undefined
                }
                placeholder={placeholder}
                readOnly={readOnly}
                ref={textareaRef}
                required={required}
                rows={autoExpand ? 1 : undefined}
                spellCheck={spellCheck}
                style={
                  resizable
                    ? { resize: "vertical", minHeight: containerHeight }
                    : undefined
                }
                value={value}
              />
              {validation && (
                <TextareaHighlightLayer
                  activeIssue={issuePopover.issue}
                  issues={issues}
                  onHoverIssue={issuePopover.hoverIssue}
                  setActiveMark={setActiveMark}
                  textareaRef={textareaRef}
                  textClassName={textClassName}
                  value={value}
                />
              )}
            </Flex>
            {(showToolbar || validation) && (
              <Flex align="center" dir="row" gap="sm" justify="flex-start">
                {showToolbar && toolbar}
                {validation && (
                  <TextareaValidationStatusIcon status={validationStatus} />
                )}
              </Flex>
            )}
            {actionButton && (
              <Flex align="center" dir="row" gap="sm" justify="flex-end">
                {actionButton}
              </Flex>
            )}
          </Flex>
          <DescriptionText
            description={hintText}
            error={theme === RedoTextareaInputFieldTheme.DESTRUCTIVE}
            size={sizeToLabelTextProps[size]}
          />
          <DescriptionText
            description={errorText}
            error
            size={sizeToLabelTextProps[size]}
          />
          {validation && (
            <>
              <span
                aria-live="polite"
                className={RedoTextareaInputFieldCss.visuallyHidden}
                id={validationMessageId}
              >
                {validationAnnouncement}
              </span>
              <TextareaIssuePopover
                anchor={activeMark}
                canResolveFix={!!validation.resolveFix}
                fixFailed={
                  !!issuePopover.issue &&
                  issuePopover.failedFixIssue === issuePopover.issue
                }
                fixing={
                  !!issuePopover.issue &&
                  issuePopover.fixingIssue === issuePopover.issue
                }
                handlers={issuePopover.popoverHandlers}
                issue={disabled || readOnly ? undefined : issuePopover.issue}
                popoverRef={issuePopover.popoverRef}
              />
            </>
          )}
          {maxLength !== undefined && (
            <Flex
              align="center"
              flexDirection="row"
              gap="xs"
              justify="flex-start"
            >
              <Text
                fontSize={sizeToLabelTextProps[size]}
                fontWeight="regular"
                textColor={themeToHintTextColor[theme]}
              >
                {t("{{numCharacters}} / {{maxCharacters}} characters", {
                  context: "character count",
                  numCharacters: value.length,
                  maxCharacters: maxLength,
                })}
              </Text>
            </Flex>
          )}
        </Flex>
      );
    },
  ),
);

const sizeStyles: Record<RedoTextareaInputFieldSize, string> = {
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  [RedoTextareaInputFieldSize.SMALL]: RedoTextareaInputFieldCss.small,
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  [RedoTextareaInputFieldSize.REGULAR]: RedoTextareaInputFieldCss.medium,
};

const sizeToTextProps: Record<RedoTextareaInputFieldSize, TextProps> = {
  [RedoTextareaInputFieldSize.SMALL]: { fontSize: "xs" },
  [RedoTextareaInputFieldSize.REGULAR]: { fontSize: "md" },
};

const sizeToLabelTextProps: Record<RedoTextareaInputFieldSize, TextSizeValue> =
  {
    [RedoTextareaInputFieldSize.SMALL]: "xs",
    [RedoTextareaInputFieldSize.REGULAR]: "sm",
  };

const sizeToPx: Record<RedoTextareaInputFieldSize, SpacingValue> = {
  [RedoTextareaInputFieldSize.SMALL]: "md",
  [RedoTextareaInputFieldSize.REGULAR]: "lg",
};

const sizeToPy: Record<RedoTextareaInputFieldSize, SpacingValue> = {
  [RedoTextareaInputFieldSize.SMALL]: "lg",
  [RedoTextareaInputFieldSize.REGULAR]: "lg",
};

const themeToHintTextColor: Record<
  RedoTextareaInputFieldTheme,
  TextColorValue
> = {
  [RedoTextareaInputFieldTheme.DEFAULT]: "secondary",
  [RedoTextareaInputFieldTheme.DESTRUCTIVE]: "error",
};
